import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import clsx from 'clsx';

import { Pagination } from 'edcom-ui';
import AppLink, { pushAppRoute } from '../components/links/AppLink';
import AdminLinks from '../components/AdminLinks/AdminLinks';
import {
  ContextLinks,
  SearchError,
  SearchFilters,
  SearchFooter,
  SearchFootnote,
  SearchResults,
  SearchTitle,
} from '../components/search';
import Facets from '../edcom-ui/components/Facets/Facets';
import Head from '../components/head';
import SelectedContentFacet from '../components/search/SearchFilters/SelectedContentFacet';
import { getSearchPageLinkingData } from '../components/LinkingData/util';
import track, { trackGA } from '../services/track';

import { replaceParams } from '../services/content';
import { CollectionsProvider } from '../services/CollectionsContext';

import styles from './search.module.less';

// EDENG-1220 - Teacher Appreciation Week
// EDENG-2065
const TAWDates = {
  begin: new Date('5/1/2023'),
  end: new Date('5/15/2023'),
};

const TAWIDs = ['175730', '175731', '175732'];

const searchTrackingFacetMap = {
  contentType: 'types',
  grades: 'grades',
  el: 'el',
  subject: 'subjects',
  topic: 'topics',
  standard: 'standards',
};

let searchTrigger = null;

export default function Search({
  total = 0,
  pages = 0,
  urlPath,
  meta,
  filter = {},
  currentFilters = [],
  facets = [],
  results = [],
  suggestions = [],
  replaceUrl = false,
  error = false,
}) {
  const router = useRouter();
  const { asPath } = router;
  const [selected, setSelected] = useState([]);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [multiSelectTracked, setMultiSelectTracked] = useState(false);
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleSelected = selected => {
    // if no selected, make sure it's view-all
    if (selected.length === 0) {
      setShowOnlySelected(false);
    } else if (selected.length > 1 && !multiSelectTracked) {
      trackGA('Search', 'Multiple items selected');
      setMultiSelectTracked(true);
    }
    setSelected(selected);
  };

  // Update URL if search response returned a different url
  React.useEffect(() => {
    if (replaceUrl) {
      router.replace('/[...search]', urlPath);
    }
    handleSelected(selected);
    setShowOnlySelected(false);
  }, [replaceUrl, asPath]);

  // Track search
  React.useEffect(() => {
    if (error || replaceUrl) {
      return;
    }

    let logEvent = searchTrigger;
    searchTrigger = null;

    if (logEvent === 'pagination') {
      return;
    }
    if (!logEvent) {
      logEvent = 'pageview';
    }

    const data = {
      nextjs: true,
      num_results: total,
      didyoumean: !!suggestions.length,
      trigger: logEvent,
      q: filter.q,
      page: filter.page,
      sort: filter.sort,
    };

    facets.forEach(({ internalName }) => {
      if (filter.facets[internalName] && searchTrackingFacetMap[internalName]) {
        const trackArray = Array.isArray(filter.facets[internalName])
          ? filter.facets[internalName]
          : [filter.facets[internalName]];
        data[searchTrackingFacetMap[internalName]] = JSON.stringify(trackArray);
      } else {
        data[searchTrackingFacetMap[internalName]] = '[]';
      }
    });

    track('Search', data);
  }, [urlPath, total, suggestions, filter]);

  const generatePaginatedUrl = page => {
    return replaceParams(urlPath, { page: page > 1 ? page : null });
  };

  const handlePagination = (e, page) => {
    e.preventDefault();
    const href = replaceParams(urlPath, { page: parseInt(page) });
    searchTrigger = 'pagination';
    pushAppRoute(router, href).then();
    if (rootRef.current != null) {
      rootRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  /**
   * @param {?string} href
   * @returns void
   */
  const handleClickSearchLink = async href => {
    if (href == null) return;

    href = replaceParams(href, { page: null });
    searchTrigger = 'filter';
    await pushAppRoute(router, href);
  };

  const handleClickSuggestion = async term => {
    const href = replaceParams(urlPath, { q: term });
    searchTrigger = 'didyoumean';
    await pushAppRoute(router, href);
  };

  const handleClearFilters = () => {
    setOpen(false);

    const queryString = urlPath
      // Just the querystring part
      .replace(/^[^?]+[?]?/, '')
      // Split into k=v parts
      .split('&')
      // Only include known search params
      .filter(val => val.match(/^(page|sort|q)=/)) // only non-facet filters
      .join('&');

    // Remove every facet except for content type when no query is defined
    let searchLink =
      filter.facets.contentType && filter.q === ''
        ? `/${filter.facets.contentType}/`
        : '/resources/';

    if (queryString.length) {
      searchLink += '?' + decodeURIComponent(queryString);
    }

    handleClickSearchLink(searchLink);
  };

  const handleClickFacet = async (e, facetHref) => {
    e.preventDefault();
    handleClickSearchLink(facetHref);
  };

  let resultShown = showOnlySelected ? selected : results;
  let pagesShown = showOnlySelected ? 1 : pages;

  // EDENG-1220 - Teacher Appreciation Week
  const date = new Date();
  const time = date.getTime();

  if (time >= TAWDates.begin && time < TAWDates.end) {
    resultShown.forEach(function(result, index) {
      if (
        resultShown[index]['type'] === 'workbook' &&
        TAWIDs.includes(resultShown[index]['id'])
      ) {
        resultShown[index]['premium'] = false;
      }
    });
  }

  return (
    <div>
      <Head
        description={meta.metaDescription}
        index={meta.index}
        path={urlPath}
        linkingData={getSearchPageLinkingData(resultShown)}
      >
        <title>{meta.metaTitle}</title>
        {filter.page * 1 === 2 && (
          <link rel="prev" href={replaceParams(urlPath, { page: null })} />
        )}
        {filter.page > 2 && (
          <link
            rel="prev"
            href={replaceParams(urlPath, { page: filter.page - 1 })}
          />
        )}
        {filter.page < pagesShown && (
          <link
            rel="next"
            href={replaceParams(urlPath, { page: filter.page * 1 + 1 })}
          />
        )}
      </Head>
      <div id="unified-search-results" ref={rootRef}>
        <div className={styles.searchResults}>
          <CollectionsProvider>
            <div className={styles.facetsWrapper}>
              {selected.length > 0 && (
                <SelectedContentFacet
                  selected={selected}
                  unSelect={handleSelected}
                  showOnlySelected={showOnlySelected}
                  setShowOnlySelected={setShowOnlySelected}
                />
              )}
              <Facets
                allFacets={facets}
                onClick={handleClickFacet}
                open={open}
                closeFilterWrap={() => setOpen(false)}
                clearFilters={handleClearFilters}
              />
            </div>
            {results.length > 0 && (
              <SearchTitle
                title={meta.title}
                contextualLinksTitle={meta.contextualLinksTitle || ''}
                contextualLinks={meta.contextualLinks || []}
              >
                {meta.contextualLinks && meta.contextualLinks.length > 0 && (
                  <ContextLinks title={meta.contextualLinksTitle}>
                    {meta.contextualLinks.map(link => (
                      <AppLink href={link.url} key={link.url}>
                        {link.title}
                      </AppLink>
                    ))}
                  </ContextLinks>
                )}
              </SearchTitle>
            )}
            {!error && results.length > 0 && (
              <SearchFilters
                total={total}
                allFacets={facets}
                filter={filter}
                currentFilters={currentFilters}
                urlPath={urlPath}
                onFilter={handleClickSearchLink}
                selected={selected}
                showOnlySelected={showOnlySelected}
                setShowOnlySelected={setShowOnlySelected}
                open={open}
                setOpen={setOpen}
                clearFilters={handleClearFilters}
              />
            )}
            <div className={clsx(styles.results, error && styles.hasError)}>
              {error && <SearchError href={urlPath} />}
              {!error && (
                <SearchResults
                  title={meta.title}
                  results={resultShown}
                  selected={selected}
                  handleSelected={handleSelected}
                  setShowOnlySelected={setShowOnlySelected}
                  suggestions={suggestions}
                  handleClickSuggestion={handleClickSuggestion}
                  currentFilter={filter}
                />
              )}
              {!error && results.length > 0 && (
                <Pagination
                  pages={pagesShown}
                  current={parseInt(filter.page)}
                  generateUrl={generatePaginatedUrl}
                  onClick={handlePagination}
                />
              )}
            </div>
          </CollectionsProvider>
          <div className={styles.clear} />

          {/*todo: only render for anon users*/}
          {filter.page === 1 && (
            <SearchFooter>
              <SearchFootnote
                title={meta.title}
                description={meta.description}
              />
              <SearchFootnote
                title={meta.bottomTitle}
                description={meta.bottomDescription}
              />
            </SearchFooter>
          )}
        </div>
      </div>
      <AdminLinks>
        <a href={`/secure/meta/searchpage.php?url=${urlPath}`} target="_blank">
          Edit Meta Info
        </a>
        <a href={`/secure/export-srp.php?path=${urlPath}`} target="_blank">
          Export Table
        </a>
      </AdminLinks>
    </div>
  );
}

const contextualLinkPropTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

const metaPropTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  metaTitle: PropTypes.string.isRequired,
  metaDescription: PropTypes.string,
  bottomTitle: PropTypes.string,
  bottomDescription: PropTypes.string,
  contextualLinksTitle: PropTypes.string,
  contextualLinks: PropTypes.arrayOf(PropTypes.shape(contextualLinkPropTypes)),
  index: PropTypes.bool,
};

const filterPropTypes = {
  q: PropTypes.string,
  page: PropTypes.number,
  sort: PropTypes.string,
  facets: PropTypes.shape({
    grades: PropTypes.arrayOf(PropTypes.string),
    contentType: PropTypes.string,
    subject: PropTypes.string,
    topic: PropTypes.string,
    standard: PropTypes.string,
    el: PropTypes.string,
    interactive: PropTypes.string,
  }),
};

const facetChildrenPropTypes = {
  urlName: PropTypes.string.isRequired,
  nesting: PropTypes.string.isRequired,
  display: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  selected: PropTypes.bool.isRequired,
  count: PropTypes.number.isRequired,
};

const facetPropTypes = {
  internalName: PropTypes.string.isRequired,
  display: PropTypes.string.isRequired,
  children: PropTypes.arrayOf(
    PropTypes.shape(facetChildrenPropTypes).isRequired
  ),
};

const resultPropTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  thumbnail: PropTypes.object,
  subject: PropTypes.string.isRequired,
  grade: PropTypes.string.isRequired,
  rating: PropTypes.number,
  new: PropTypes.bool.isRequired,
  answersheet: PropTypes.bool.isRequired,
  premium: PropTypes.bool,
  el: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  meta: PropTypes.object,
  isInteractive: PropTypes.bool,
};

const currentFiltersPropTypes = {
  display: PropTypes.string,
  group: PropTypes.string,
  href: PropTypes.string,
};

Search.propTypes = {
  total: PropTypes.number,
  pages: PropTypes.number,
  urlPath: PropTypes.string.isRequired,
  meta: PropTypes.shape(metaPropTypes).isRequired,
  filter: PropTypes.shape(filterPropTypes),
  currentFilters: PropTypes.arrayOf(PropTypes.shape(currentFiltersPropTypes)),
  facets: PropTypes.arrayOf(PropTypes.shape(facetPropTypes)),
  results: PropTypes.arrayOf(PropTypes.shape(resultPropTypes)),
  suggestions: PropTypes.arrayOf(PropTypes.string),
  replaceUrl: PropTypes.bool,
  error: PropTypes.bool,
};
